import { deleteApi, patchApi, postApi, getApi, ParamsType } from "./resonanceApi";
import { AxiosResponse } from 'axios';
import { LoginResponseType, LoginType, PostUserGamification } from "../types/welcome";
import { UserType } from "../types/session";
import { otpChallengeStatusResponse, postChangePasswordBody, postInitIdentificationType, requestEmailChangeBody, requestNicknameChangeBody, requestPhoneNumberChangeBody, requestSignupEmailVerificationBody, requestSignupPhoneNumberVerificationBody, ValidateUserInfoRequestBody, ValidateUserInfoResponse, verificationCodeBody } from "../types/user.types";
import { InclusionAnswerType, PatchUserType, postConsentRequestType, PostInclusionQuestionType, PostSiteType, PostUserResponseType, PostUserType, StratQuestionType } from "../types/welcome";

export interface ConsentReservationResponse {
  consent_id: number | null;
  reserved_until: string | null;
}

export interface RefreshConsentReservationResponse {
  reserved_until: string | null;
}

export enum UserApiUrlsEnum {
  PATCH_LANG = 'studies/{studyId}/users/current',
  LOGIN = 'studies/{studyId}/roles/{roleId}/sessions',
  LOGOUT = 'resonance/sessions',
  GET_CURRENT_USER = 'studies/{studyId}/users/current',
  CHANGE_PWD = 'studies/{studyId}/users/password',
  POST_INCLUSION = 'studies/{studyId}/users/{userId}/inclusion_questions',
  POST_TEST_ACCOUNT = 'studies/{studyId}/users/{userId}/test_account',
  POST_USER = 'studies/{studyId}/users',
  PATCH_USER = 'studies/{studyId}/users/{userId}',
  POST_GAMIFICATION = 'studies/{studyId}/users/{userId}/gamification',
  GET_STRAT_QUESTIONS = 'studies/{studyId}/roles/{roleId}/stratification_questions',
  GET_CONSENT_BY_STRAT_QUESTIONS = 'studies/{studyId}/roles/{roleId}/stratification_questions/getconsent',
  SET_SITE = 'studies/{studyId}/users/current/sites',
  SET_CONSENT = 'studies/{studyId}/users/{userId}/consents',
  POST_INIT_IDENTIFICATION = 'studies/{studyId}/users/init_identification',
  VALIDATE_USER_INFO = 'studies/{studyId}/users/current/check',
  FAMILY_PRECHECK = 'studies/{studyId}/users/current/family/precheck',
  FORGOT_PASSWORD = 'studies/{studyId}/users/forgotten_password',
  REST_PASSWORD = 'studies/{studyId}/users/reset_password',
  GET_TOTAL_USER = 'studies/{studyId}/users/total',
  REQUEST_EMAIL_CHANGE = 'studies/{studyId}/users/current/email_change/request',
  VERIFY_EMAIL_CHANGE = 'studies/{studyId}/users/current/email_change/verify',
  RESEND_EMAIL_CHANGE = 'studies/{studyId}/users/current/email_change/resend',
  CANCEL_EMAIL_CHANGE = 'studies/{studyId}/users/current/email_change/cancel',
  REQUEST_PHONE_NUMBER_CHANGE = 'studies/{studyId}/users/current/phone_number_change/request',
  VERIFY_PHONE_NUMBER_CHANGE = 'studies/{studyId}/users/current/phone_number_change/verify',
  RESEND_PHONE_NUMBER_CHANGE = 'studies/{studyId}/users/current/phone_number_change/resend',
  CANCEL_PHONE_NUMBER_CHANGE = 'studies/{studyId}/users/current/phone_number_change/cancel',
  REQUEST_SIGNUP_EMAIL_VERIFICATION = 'studies/{studyId}/users/current/signup/email/request',
  VERIFY_SIGNUP_EMAIL_VERIFICATION = 'studies/{studyId}/users/current/signup/email/verify',
  RESEND_SIGNUP_EMAIL_VERIFICATION = 'studies/{studyId}/users/current/signup/email/resend',
  CANCEL_SIGNUP_EMAIL_VERIFICATION = 'studies/{studyId}/users/current/signup/email/cancel',
  REQUEST_SIGNUP_PHONE_NUMBER_VERIFICATION = 'studies/{studyId}/users/current/signup/phone_number/request',
  VERIFY_SIGNUP_PHONE_NUMBER_VERIFICATION = 'studies/{studyId}/users/current/signup/phone_number/verify',
  RESEND_SIGNUP_PHONE_NUMBER_VERIFICATION = 'studies/{studyId}/users/current/signup/phone_number/resend',
  CANCEL_SIGNUP_PHONE_NUMBER_VERIFICATION = 'studies/{studyId}/users/current/signup/phone_number/cancel',
  REQUEST_NICKNAME_CHANGE = 'studies/{studyId}/users/current/nickname_change/request',
  POST_NIR = 'studies/{studyId}/users/current/nir',
  GET_NIR_STATUS = 'studies/{studyId}/users/current/nir',
  POST_NIR_OPPOSITION = 'studies/{studyId}/users/current/nir/opposition',
  PATCH_NOTIFICATION_PREFERENCES = 'studies/{studyId}/users/current/notification_preferences',
  PUBLIC_NOTIFICATION_PREFERENCES = 'notifications/preferences',
}

enum UserCurrentApiUrlsEnum {
  REFRESH_CONSENT_RESERVATION = 'studies/{studyId}/users/current/consent_reservation/refresh',
}

export const postInclusionAnswers = (
  studyId: number,
  userId: number,
  answers: InclusionAnswerType[]
): Promise<AxiosResponse<PostInclusionQuestionType>> => {
  const url = UserApiUrlsEnum.POST_INCLUSION;
  return postApi<PostInclusionQuestionType>(url, { studyId, userId }, answers);
};

export const postTestAccount = (studyId: number, userId: number): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.POST_TEST_ACCOUNT;
  return postApi<void>(url, { studyId, userId }, {});
}

export const postNewUser = (studyId: number, userInfos: PostUserType): Promise<AxiosResponse<PostUserResponseType>> => {
  const url = UserApiUrlsEnum.POST_USER;
  return postApi<PostUserResponseType>(url, { studyId }, userInfos);
};

export const patchUser = (studyId: number, userId: number, userInfos: PatchUserType): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.PATCH_USER;
  return patchApi<void>(url, { studyId, userId }, userInfos);
};

export const postUserGamification = (studyId: number, userId: number, userInfos: PostUserGamification): Promise<AxiosResponse<PostUserGamification>> => {
  const url = UserApiUrlsEnum.POST_GAMIFICATION;
  const res = postApi<PostUserGamification>(url, { studyId, userId }, userInfos);
  return res;
};

export const getStratificationQuestions = ({ studyId, roleId }: ParamsType): Promise<AxiosResponse<StratQuestionType[]>> => {
  const url = UserApiUrlsEnum.GET_STRAT_QUESTIONS;
  return getApi<StratQuestionType[]>(url, { studyId, roleId });
};

export const postConsentByStratificationQuestions = ({
  studyId,
  roleId,
  stratification_questions,
  birthdate,
}: {
  studyId: number;
  roleId: number;
  stratification_questions: NonNullable<postConsentRequestType["stratification_questions"]>;
  // Sent so deterministic (age-based) profiling can resolve the arm/consent at preview time,
  // before the birthdate is persisted with the final consent submission.
  birthdate?: string | null;
}): Promise<AxiosResponse<ConsentReservationResponse>> => {
  const url = UserApiUrlsEnum.GET_CONSENT_BY_STRAT_QUESTIONS;
  return postApi<ConsentReservationResponse>(url, { studyId, roleId }, { stratification_questions, birthdate });
}

export const refreshConsentReservation = (studyId: number): Promise<AxiosResponse<RefreshConsentReservationResponse>> => {
  const url = UserCurrentApiUrlsEnum.REFRESH_CONSENT_RESERVATION;
  return postApi<RefreshConsentReservationResponse>(url, { studyId }, {});
}

export const postSite = (studyId: number, body: PostSiteType): Promise<AxiosResponse> => {
  const url = UserApiUrlsEnum.SET_SITE;
  return postApi(url, { studyId }, body);
};

// NIR (SNDS appariement): the parent's self-declared number travels ONLY
// through this dedicated endpoint (bearer session) — never in the consent
// payload or any profile field. Called at CONSENT time (the value is held
// client-side until then). Response is 204 (never echoed).
export const postNir = (studyId: number, body: { nir: string }): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.POST_NIR;
  return postApi<void>(url, { studyId }, body);
};

// Booleans only — feeds the profile page's opposition control.
export interface NirStatusResponse {
  exists: boolean;
  opposed: boolean;
}

export const getNirStatus = (studyId: number): Promise<AxiosResponse<NirStatusResponse>> => {
  const url = UserApiUrlsEnum.GET_NIR_STATUS;
  return getApi<NirStatusResponse>(url, { studyId });
};

// Droit d'opposition (self-service): excludes the caller's NIR from every
// future SNDS linkage export. Idempotent; also fired automatically when
// leaving the study.
export const postNirOpposition = (studyId: number): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.POST_NIR_OPPOSITION;
  return postApi<void>(url, { studyId }, {});
};

// lang = the UI language the consent was actually DISPLAYED in (the same i18n value the
// consent content was fetched with): the API records it on the per-checkbox acceptance
// rows (label_snapshot language). Without it the API falls back to the study primary
// language and would snapshot wording the participant never saw.
export const postConsent = (studyId: number, userId: number, body: postConsentRequestType, lang?: string) => {
  const url = UserApiUrlsEnum.SET_CONSENT;
  return postApi(url, { studyId, userId }, body, lang ? { lang } : {})
}

export const login = (studyId: number, roleId: number, body: LoginType): Promise<AxiosResponse<LoginResponseType>> => {
  const url = UserApiUrlsEnum.LOGIN;
  return postApi<LoginResponseType>(url, { studyId, roleId }, body);
}

export const logout = (): Promise<AxiosResponse<LoginResponseType>> => {
  const url = UserApiUrlsEnum.LOGOUT;
  return deleteApi<LoginResponseType>(url);
}

export const getCurrentUser = (studyId: number): Promise<AxiosResponse<UserType>> => {
  const url = UserApiUrlsEnum.GET_CURRENT_USER;
  return getApi<UserType>(url, { studyId });
};
export const changePassword = (params: { studyId: number }, body: postChangePasswordBody): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.CHANGE_PWD;
  return patchApi(url, params, body)
}

export const forgotPassword = (params: { studyId: number }, body: { login: string }): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.FORGOT_PASSWORD;
  return postApi(url, params, body)
}

export const resetPassword = (params: { studyId: number }, body: { login: string, reset_password_token: string, password: string, password_confirmation: string }): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.REST_PASSWORD;
  return postApi(url, params, body)
}


export const patchLang = (studyId: number, body: { lang: string }): Promise<AxiosResponse> => {
  const url = UserApiUrlsEnum.PATCH_LANG;
  return patchApi(url, { studyId }, body);
}

export interface UpdateNotificationPreferencesBody {
  email?: boolean;
  sms?: boolean;
  push?: boolean;
}

export const patchNotificationPreferences = (
  studyId: number,
  body: UpdateNotificationPreferencesBody
): Promise<AxiosResponse<UserType>> => {
  const url = UserApiUrlsEnum.PATCH_NOTIFICATION_PREFERENCES;
  return patchApi<UserType>(url, { studyId }, body);
}

export interface PublicNotificationPreferences {
  email: boolean;
  sms: boolean;
  push: boolean;
  study: { name: string; lang: string };
}

// Public (no-login) notification-preference endpoints reached from the unsubscribe
// email link. The signed token in the query identifies the user — no auth header.
export const getPublicNotificationPreferences = (
  token: string
): Promise<AxiosResponse<PublicNotificationPreferences>> => {
  return getApi<PublicNotificationPreferences>(
    UserApiUrlsEnum.PUBLIC_NOTIFICATION_PREFERENCES,
    {},
    { token }
  );
}

export const patchPublicNotificationPreferences = (
  token: string,
  body: UpdateNotificationPreferencesBody
): Promise<AxiosResponse<PublicNotificationPreferences>> => {
  const url = `${UserApiUrlsEnum.PUBLIC_NOTIFICATION_PREFERENCES}?token=${encodeURIComponent(token)}`;
  return patchApi<PublicNotificationPreferences>(url, {}, body);
}


export const postInitIdentification = (params: { studyId: number }, body: postInitIdentificationType): Promise<AxiosResponse<PostUserResponseType>> => {
  const url = UserApiUrlsEnum.POST_INIT_IDENTIFICATION;
  return postApi(url, params, body);
}

export const validateUserInfo = (params: { studyId: number }, body: ValidateUserInfoRequestBody): Promise<AxiosResponse<ValidateUserInfoResponse>> => {
  const url = UserApiUrlsEnum.VALIDATE_USER_INFO;
  return postApi(url, params, body);
}

// Family composition: counts of the study's auto-creatable data-only members already
// sharing this numéro de dossier (drives the signup step's skip logic).
export const postFamilyPrecheck = (
  params: { studyId: number },
  body: { external_id: string }
): Promise<AxiosResponse<{ counts: Record<number, number> }>> => {
  const url = UserApiUrlsEnum.FAMILY_PRECHECK;
  return postApi(url, params, body);
}

export const getTotalUser = (studyId: number): Promise<AxiosResponse> => {
  const url = UserApiUrlsEnum.GET_TOTAL_USER;
  return getApi(url, { studyId });
};

export const requestEmailChange = (params: { studyId: number }, body: requestEmailChangeBody): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.REQUEST_EMAIL_CHANGE;
  return postApi(url, params, body);
}

export const requestPhoneNumberChange = (params: { studyId: number }, body: requestPhoneNumberChangeBody): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.REQUEST_PHONE_NUMBER_CHANGE;
  return postApi(url, params, body);
}

export const requestNicknameChange = (params: { studyId: number }, body: requestNicknameChangeBody): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.REQUEST_NICKNAME_CHANGE;
  return postApi(url, params, body);
}

export const verifyEmailChange = (params: { studyId: number }, body: verificationCodeBody): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.VERIFY_EMAIL_CHANGE;
  return postApi(url, params, body);
}

export const resendEmailChangeCode = (params: { studyId: number }): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.RESEND_EMAIL_CHANGE;
  return postApi(url, params, {});
}

export const cancelEmailChange = (params: { studyId: number }): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.CANCEL_EMAIL_CHANGE;
  return postApi(url, params, {});
}

export const requestSignupEmailVerification = (params: { studyId: number }, body: requestSignupEmailVerificationBody): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.REQUEST_SIGNUP_EMAIL_VERIFICATION;
  return postApi(url, params, body);
}

export const verifySignupEmailVerification = (params: { studyId: number }, body: verificationCodeBody): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.VERIFY_SIGNUP_EMAIL_VERIFICATION;
  return postApi(url, params, body);
}

export const resendSignupEmailVerification = (params: { studyId: number }): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.RESEND_SIGNUP_EMAIL_VERIFICATION;
  return postApi(url, params, {});
}

export const cancelSignupEmailVerification = (params: { studyId: number }): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.CANCEL_SIGNUP_EMAIL_VERIFICATION;
  return postApi(url, params, {});
}

export const verifyPhoneNumberChange = (params: { studyId: number }, body: verificationCodeBody): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.VERIFY_PHONE_NUMBER_CHANGE;
  return postApi(url, params, body);
}

export const resendPhoneNumberChangeCode = (params: { studyId: number }): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.RESEND_PHONE_NUMBER_CHANGE;
  return postApi(url, params, {});
}

export const cancelPhoneNumberChange = (params: { studyId: number }): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.CANCEL_PHONE_NUMBER_CHANGE;
  return postApi(url, params, {});
}

export const requestSignupPhoneNumberVerification = (params: { studyId: number }, body: requestSignupPhoneNumberVerificationBody): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.REQUEST_SIGNUP_PHONE_NUMBER_VERIFICATION;
  return postApi(url, params, body);
}

export const verifySignupPhoneNumberVerification = (params: { studyId: number }, body: verificationCodeBody): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.VERIFY_SIGNUP_PHONE_NUMBER_VERIFICATION;
  return postApi(url, params, body);
}

export const resendSignupPhoneNumberVerification = (params: { studyId: number }): Promise<AxiosResponse<otpChallengeStatusResponse>> => {
  const url = UserApiUrlsEnum.RESEND_SIGNUP_PHONE_NUMBER_VERIFICATION;
  return postApi(url, params, {});
}

export const cancelSignupPhoneNumberVerification = (params: { studyId: number }): Promise<AxiosResponse<void>> => {
  const url = UserApiUrlsEnum.CANCEL_SIGNUP_PHONE_NUMBER_VERIFICATION;
  return postApi(url, params, {});
}